// language-onboarding.js — first-run two-step opening dialogue (Brief 004a,
// faithful to the Unity TwoStepLanguageHandler).
//
// Two steps, both built from content/languages.json (no language hardcoded —
// Gotcha 3):
//   1. "What language do you speak?"  → mother tongue (L1), from `motherTongues`,
//      each shown in its OWN native name so the picker reads correctly before
//      any UI language is chosen (Brief 004a SC#1). Picking one APPLIES that L1
//      (loads its i18n pack) and advances — mirrors Unity: source picked →
//      labels known.
//   2. "What do you want to learn?"   → target course, from `targetCourses()`,
//      rendered in the just-chosen L1. The available target is selectable;
//      `comingSoonTargets` are greyed with a "Coming Soon" badge (Unity's
//      greyed-button pattern), not hidden.
//
// On finish: persist the target axis (L1 was already persisted in Step 1) and
// route to /dashboard. Re-openable from the shell's 🌐 affordance — pre-selects
// the current choices.
//
// REMOUNT BRIDGE (Brief 004 Issue #4 / Gotcha 2): applying the L1 fires
// `kfe:i18n`, which makes the shell remount THIS screen (router.render() builds
// a fresh element, so instance state is lost). The module-scoped `draft` carries
// the in-progress step/selection across that one remount so Step 2 renders in the
// chosen language instead of snapping back to Step 1. It's a one-shot token —
// consumed (cleared) the moment the remounted instance reads it.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import * as router from "../router.js";
import * as lang from "../i18n/language-manager.js";

/** @type {{ step: number, l1: string, l2: string } | null} */
let draft = null;

export class L2BLanguageOnboarding extends KFEComponent {
  static styles = `
    :host { display: block; }
    /* The opening dialogue sits in a framed dojo panel (Panel UI 9-slice; teal
       centre painted by the slice 'fill'). Falls back to a flat surface if no
       skin panel token is set (plain theme). */
    .wrap {
      max-width: 30rem; margin: 2.5rem auto;
      padding: 0.5rem 1.25rem;
      background: var(--l2b-surface); border-radius: var(--l2b-radius);
      border: var(--l2b-panel-border) solid transparent;
      border-image: var(--l2b-skin-panel) var(--l2b-panel-slice) / var(--l2b-panel-border) stretch;
      filter: drop-shadow(0 6px 18px rgba(0,0,0,0.5));
    }
    h2 { font-size: var(--l2b-h2); margin: 0.5rem 0 1.25rem; text-align: center;
         color: var(--l2b-fg); font-weight: 800; text-shadow: 0 1px 2px rgba(0,0,0,0.5); }
    ul { list-style: none; margin: 0 0 1.5rem; padding: 0;
         display: flex; flex-direction: column; gap: 0.5rem; }
    button.option {
      display: flex; align-items: center; gap: 0.6rem; width: 100%;
      text-align: left; font: inherit; cursor: pointer;
      padding: 0.85rem 1rem; border-radius: var(--l2b-radius);
      background: rgba(15, 30, 31, 0.55); color: var(--l2b-fg);
      border: 2px solid var(--l2b-border);
      transition: background 0.12s ease, border-color 0.12s ease;
    }
    button.option:hover { background: rgba(44, 80, 82, 0.7); }
    button.option[aria-pressed="true"] {
      border-color: var(--l2b-accent);
      background: color-mix(in srgb, var(--l2b-accent) 22%, rgba(15, 30, 31, 0.55));
      box-shadow: 0 0 0 1px var(--l2b-accent), 0 0 12px rgba(224, 85, 43, 0.5);
    }
    button.option[disabled] {
      cursor: default; opacity: 0.5;
      background: rgba(15, 30, 31, 0.4); border-style: dashed;
    }
    button.option[disabled]:hover { background: rgba(15, 30, 31, 0.4); }
    .option .check { color: var(--l2b-accent); font-weight: 700; width: 1rem; }
    .option .label { flex: 1; }
    .option .badge {
      font-size: 0.7rem; font-weight: 700; letter-spacing: 0.02em;
      text-transform: uppercase; padding: 0.15rem 0.5rem;
      border-radius: 999px; background: rgba(0,0,0,0.3); color: var(--l2b-muted);
      border: 1px solid var(--l2b-border);
    }
    .nav { display: flex; justify-content: space-between; gap: 0.75rem; align-items: center; }
    button.cta, button.ghost {
      font: inherit; cursor: pointer; font-weight: 800;
      padding: 0.7rem 1.6rem; border-radius: var(--l2b-radius);
    }
    /* Primary CTA = dojo button sprite (cream centre → dark ink). */
    button.cta {
      color: #4a2f1a; background: transparent;
      border: var(--l2b-btn-border) solid transparent;
      border-image: var(--l2b-skin-btn) var(--l2b-btn-slice) / var(--l2b-btn-border) stretch;
      filter: drop-shadow(0 3px 6px rgba(0,0,0,0.45));
      transition: transform 0.1s ease, filter 0.1s ease;
    }
    button.cta:hover { filter: drop-shadow(0 5px 10px rgba(0,0,0,0.5)) brightness(1.04); }
    button.cta:active { transform: translateY(1px); }
    button.cta:disabled { opacity: 0.5; cursor: default; }
    button.ghost { background: rgba(0,0,0,0.25); color: var(--l2b-fg);
                   border: 1px solid var(--l2b-border); }
    button.ghost:hover { background: rgba(0,0,0,0.4); }
  `;

  constructor() {
    super();
    this._step = 1;          // 1 = mother tongue, 2 = target course
    this._l1 = "";           // selected L1 code
    this._l2 = "";           // selected L2 (target course) code
    this._ready = false;
  }

  async onConnected() {
    // Registry only — never re-applies the i18n pack here (would remount us).
    await lang.ensureLoaded();

    if (draft) {
      // We were just remounted after applying the L1 in Step 1 — restore the
      // in-progress selection and consume the one-shot bridge.
      this._step = draft.step;
      this._l1 = draft.l1;
      this._l2 = this._validTarget(draft.l2);
      draft = null;
    } else {
      // Fresh mount. On a re-open (stored choices) pre-select them; on a true
      // first launch leave L1 unselected so nothing is pre-greeted (SC#1).
      const cur = lang.current();
      const stored = lang.hasStoredChoices();
      this._step = 1;
      this._l1 = stored ? cur.motherTongue : "";
      this._l2 = this._validTarget(stored ? cur.targetLanguage : "");
    }
    this._ready = true;
    this._scheduleRender();
  }

  /**
   * Coerce a target code to one that's actually selectable for the current L1
   * (Brief 004b: the language you already speak is greyed, so it can't be the
   * default). Falls back to the first selectable course.
   * @param {string} code
   * @returns {string}
   */
  _validTarget(code) {
    const courses = lang.targetCourses(this._l1);
    if (courses.some((c) => c.code === code && c.available)) return code;
    return courses.find((c) => c.available)?.code ?? "";
  }

  /**
   * Step 1 answered. Apply the chosen L1 (loads its i18n pack + fires kfe:i18n)
   * and advance to Step 2. The kfe:i18n remount recreates this element, so we
   * stash the target step in `draft` first; the remounted instance restores it.
   * @param {string} code
   */
  async _selectL1(code) {
    this._l1 = code;
    // The previously-defaulted target may be the language we just chose as L1
    // (now greyed) — re-validate so Step 2 lands on a selectable course.
    this._l2 = this._validTarget(this._l2);
    draft = { step: 2, l1: code, l2: this._l2 };
    await lang.setMotherTongue(code);   // persists + applies i18n + fires change
    // Fallback if no shell remounted us (e.g. in isolation): advance in place.
    this._step = 2;
    this._scheduleRender();
  }

  /** @param {string} code */
  _selectL2(code) { this._l2 = code; this._scheduleRender(); }

  _back() { this._step = 1; this._scheduleRender(); }

  _finish() {
    if (!this._l1 || !this._l2) return;
    // L1 was already applied + persisted in Step 1; just persist the target axis.
    lang.setTarget(this._l2);
    draft = null;
    router.navigate("/dashboard");
  }

  /**
   * Step-1 list: every mother tongue in its own native name (SC#1). Picking one
   * applies it and advances.
   * @returns {HTMLElement}
   */
  _renderL1List() {
    const ul = document.createElement("ul");
    for (const e of lang.motherTongues()) {
      const li = document.createElement("li");
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "option";
      btn.setAttribute("aria-pressed", String(e.code === this._l1));
      const check = document.createElement("span");
      check.className = "check";
      check.textContent = e.code === this._l1 ? "✓" : "";
      const label = document.createElement("span");
      label.className = "label";
      label.textContent = e.name;   // native name — already locale-correct
      btn.append(check, label);
      btn.addEventListener("click", () => this._selectL1(e.code));
      li.appendChild(btn);
      ul.appendChild(li);
    }
    return ul;
  }

  /**
   * Step-2 list: target courses rendered in the chosen L1 (t()). The available
   * course is selectable; coming-soon courses are disabled + badged.
   * @returns {HTMLElement}
   */
  _renderL2List() {
    const ul = document.createElement("ul");
    // Exclude the language the learner already speaks (Brief 004b): it's greyed
    // "you already speak this", while any genuinely future course is greyed
    // "coming soon" — same Unity greyed-button pattern, different reason.
    for (const c of lang.targetCourses(this._l1)) {
      const li = document.createElement("li");
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "option";
      btn.disabled = !c.available;
      btn.setAttribute("aria-pressed", String(c.available && c.code === this._l2));

      const check = document.createElement("span");
      check.className = "check";
      check.textContent = c.available && c.code === this._l2 ? "✓" : "";

      const label = document.createElement("span");
      label.className = "label";
      label.textContent = t(`onboarding.learn.${c.code}`);

      btn.append(check, label);
      if (!c.available) {
        const badge = document.createElement("span");
        badge.className = "badge";
        badge.textContent = c.reason === "speaksAlready"
          ? t("onboarding.speaksAlready")
          : t("onboarding.comingSoon");
        btn.appendChild(badge);
      } else {
        btn.addEventListener("click", () => this._selectL2(c.code));
      }
      li.appendChild(btn);
      ul.appendChild(li);
    }
    return ul;
  }

  render() {
    if (!this._ready) return html`<p>${t("loading")}</p>`;

    if (this._step === 1) {
      // Heading reads in the neutral opening pack on first launch (English, the
      // lingua-franca fallback) and in the chosen L1 on re-open — never the
      // default L1's Mandarin (Brief 004a SC#1).
      return html`
        <section class="wrap">
          <h2>${t("onboarding.askL1")}</h2>
          ${this._renderL1List()}
        </section>
      `;
    }

    // Step 2 — target course, shown in the chosen L1.
    const back = document.createElement("button");
    back.type = "button";
    back.className = "ghost";
    back.textContent = t("onboarding.back");
    back.addEventListener("click", () => this._back());

    const finish = document.createElement("button");
    finish.type = "button";
    finish.className = "cta";
    finish.textContent = t("onboarding.finish");
    finish.disabled = !this._l2;
    finish.addEventListener("click", () => this._finish());

    return html`
      <section class="wrap">
        <h2>${t("onboarding.askL2")}</h2>
        ${this._renderL2List()}
        <div class="nav">${back}${finish}</div>
      </section>
    `;
  }
}
